import React, { useMemo } from 'react';
import PrimaryButton from '../../../components/Buttons/PrimaryButton';
import { useRoundCache } from '../../../hooks/useRoundCache';
import {
  ActionRow,
  CounterRow,
  MissionHeader,
  MissionRow,
  OptionCard,
  OptionsGroup,
  ScoreSummary,
} from './styles';
import { textSize } from '../../../styles/brand';
import {
  formatMissionPoints,
  formatPoints,
  formatSigned,
} from '../../../utils/tournament/points';
interface IModalityRef {
  evaluation_type: 'binary' | 'subjective';
  bonus_time_seconds: number;
  bonus_points: number;
  penalty_per_occurrence: number;
  max_score?: number;
}

interface IEvaluationSubmitData {
  mission_scores: { mission_order: number; score: number }[];
  touch_count?: number;
  infraction_count?: number;
}

interface ISeguidorLinhaFormProps {
  roundId: string;
  modalityRef: IModalityRef;
  timerSeconds: number;
  onSubmit: (data: IEvaluationSubmitData) => Promise<void>;
  isSubmitting: boolean;
}

const MISSIONS = [1, 2, 3, 4, 5];
const POINTS_PER_MISSION = 20;

type TMissionStatus = 'sim' | 'nao' | 'nulo';

const OPTIONS = [
  { label: 'Sim', value: 'sim', points: POINTS_PER_MISSION },
  { label: 'Não', value: 'nao', points: 0 },
  { label: 'Nulo', value: 'nulo', points: 0 },
];

export const SeguidorLinhaForm: React.FC<ISeguidorLinhaFormProps> = ({
  roundId,
  modalityRef,
  timerSeconds,
  onSubmit,
  isSubmitting,
}) => {
  const [state, setState, clearCache] = useRoundCache(
    `round_evaluate_${roundId}_seguidorlinha`,
    {
      missions: {} as Record<number, TMissionStatus>,
      touches: 0,
    },
  );

  const handleSelect = (mission: number, value: TMissionStatus) => {
    setState(prev => ({
      ...prev,
      missions: { ...prev.missions, [mission]: value },
    }));
  };

  const handleTouchChange = (delta: number) => {
    setState(prev => ({
      ...prev,
      touches: Math.max(0, prev.touches + delta),
    }));
  };

  const missionSum = useMemo(
    () =>
      Object.values(state.missions).reduce((acc, val) => {
        return acc + (val === 'sim' ? POINTS_PER_MISSION : 0);
      }, 0),
    [state.missions],
  );

  const maxScore = MISSIONS.length * POINTS_PER_MISSION;

  // Bonus applied if all are answered and none is "nao"
  const hasAnyNao = Object.values(state.missions).includes('nao');
  const allAnswered = MISSIONS.every(m => state.missions[m] !== undefined);

  const bonusApplied =
    allAnswered &&
    !hasAnyNao &&
    timerSeconds > 0 &&
    timerSeconds < modalityRef.bonus_time_seconds;

  const bonusScore = bonusApplied ? modalityRef.bonus_points : 0;
  const penaltyScore = state.touches * modalityRef.penalty_per_occurrence;
  const totalScore = Math.max(0, missionSum + bonusScore - penaltyScore);

  const handleSubmit = async () => {
    const mission_scores = MISSIONS.map(m => ({
      mission_order: m,
      score: state.missions[m] === 'sim' ? POINTS_PER_MISSION : 0,
    }));
    await onSubmit({ mission_scores, touch_count: state.touches });
    clearCache();
  };

  return (
    <>
      {MISSIONS.map(mission => {
        const selectedValue = state.missions[mission];
        return (
          <MissionRow key={mission}>
            <MissionHeader>
              <h4>Missão {mission}</h4>
              <span>
                {formatMissionPoints(
                  selectedValue === undefined
                    ? undefined
                    : selectedValue === 'sim'
                      ? POINTS_PER_MISSION
                      : 0,
                )}
              </span>
            </MissionHeader>
            <OptionsGroup>
              {OPTIONS.map(opt => (
                <OptionCard
                  key={opt.value}
                  type="button"
                  $selected={selectedValue === opt.value}
                  aria-pressed={selectedValue === opt.value}
                  $isZero={opt.points === 0}
                  onClick={() =>
                    handleSelect(mission, opt.value as TMissionStatus)
                  }
                >
                  <span className="label">{opt.label}</span>
                  <span className="points">{formatPoints(opt.points)}</span>
                </OptionCard>
              ))}
            </OptionsGroup>
          </MissionRow>
        );
      })}

      <CounterRow>
        <span className="label">
          Toques (−{modalityRef.penalty_per_occurrence} pts cada)
        </span>
        <div className="controls">
          <button
            type="button"
            aria-label="Remover um toque"
            onClick={() => handleTouchChange(-1)}
            disabled={state.touches === 0}
          >
            −
          </button>
          <span className="value">{state.touches}</span>
          <button
            type="button"
            className="orange"
            aria-label="Adicionar um toque"
            onClick={() => handleTouchChange(1)}
          >
            +
          </button>
        </div>
      </CounterRow>

      <ScoreSummary>
        <div className="box">
          <span className="label">Missões</span>
          <span className="value">
            {missionSum}/{maxScore}
          </span>
        </div>
        <div className="box">
          <span className="label">Bônus</span>
          <span className={`value${bonusScore ? ' bonus' : ''}`}>
            {formatSigned(bonusScore, '+')}
          </span>
        </div>
        <div className="box">
          <span className="label">Penalidade</span>
          <span className={`value${penaltyScore ? ' penalty' : ''}`}>
            {formatSigned(penaltyScore, '-')}
          </span>
        </div>
        <div className="box">
          <span className="label">Total da rodada</span>
          <span className="value total">{totalScore}</span>
        </div>
      </ScoreSummary>

      <ActionRow>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            gap: '0.25rem',
            width: '100%',
          }}
        >
          <PrimaryButton
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting || timerSeconds === 0 || !allAnswered}
          >
            {isSubmitting ? 'Registrando...' : 'Registrar avaliação'}
          </PrimaryButton>
          {timerSeconds === 0 && (
            <span style={{ fontSize: textSize(0.78), color: '#9ca3af' }}>
              Inicie o cronômetro antes de registrar
            </span>
          )}
          {timerSeconds > 0 && !allAnswered && (
            <span style={{ fontSize: textSize(0.78), color: '#9ca3af' }}>
              Preencha todas as missões para continuar
            </span>
          )}
        </div>
      </ActionRow>
    </>
  );
};
